import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Copy, Gift, RefreshCw } from 'lucide-react';
import { ReferralStatus } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { serverMessage } from '~/lib/api/server-error';
import { useCustomerReferrals, useRegenerateReferralCode } from '~/hooks/use-customer-subviews';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	customerId: number | null;
	customerName?: string;
	/** Opens another customer (the referrer, a referred customer) in the editor. */
	onOpenCustomer?: (id: number) => void;
}

const dateOnly = (v: unknown) => (v ? String(v).slice(0, 10) : '—');

/**
 * BW-8460 (RF-13): a customer's refer-a-friend record — their code, who referred them, and the customers who joined
 * with their codes and where each reward stands — so support can answer "where are my referral points" and spot abuse.
 * Regenerating the code expires the old one: invitations already sent stop applying.
 */
export function CustomerReferralsDialog({ open, onOpenChange, customerId, customerName, onOpenCustomer }: Props) {
	const { t } = useTranslation();
	const { data, isLoading } = useCustomerReferrals(open ? customerId : null);
	const regenerate = useRegenerateReferralCode(customerId ?? 0);
	const [confirming, setConfirming] = useState(false);

	useEffect(() => {
		if (!open) setConfirming(false);
	}, [open]);

	const copy = (code: string) =>
		Promise.resolve()
			.then(() => navigator.clipboard.writeText(code))
			.then(() => toast.success(t('customers.list.referrals.copied')))
			.catch(() => toast.error(t('customers.list.referrals.copyFailed')));

	const doRegenerate = () =>
		regenerate.mutate(undefined, {
			onSuccess: () => {
				toast.success(t('customers.list.referrals.regenerated'));
				setConfirming(false);
			},
			onError: e => toast.error(serverMessage(e))
		});

	const statusLabel = (status?: ReferralStatus, points?: number) =>
		status === ReferralStatus.Rewarded
			? t('customers.list.referrals.statusRewarded', { points: points ?? 0 })
			: status === ReferralStatus.Reversed
				? t('customers.list.referrals.statusReversed')
				: t('customers.list.referrals.statusJoined');

	const items = data?.items ?? [];

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-[95vw] sm:max-w-3xl">
				<DialogHeader>
					<DialogTitle>{t('customers.list.referrals.title')}</DialogTitle>
					<DialogDescription>{customerName || t('customers.list.referrals.subtitle')}</DialogDescription>
				</DialogHeader>

				{isLoading ? (
					<div className="flex flex-col gap-2">
						{Array.from({ length: 4 }).map((_, i) => (
							<Skeleton key={i} className="h-9 w-full rounded-md" />
						))}
					</div>
				) : (
					<div className="flex flex-col gap-4">
						<div className="grid gap-3 sm:grid-cols-2">
							<div className="rounded-md border border-border px-3 py-2">
								<p className="text-xs text-foreground-muted">{t('customers.list.referrals.code')}</p>
								<div className="flex items-center justify-between gap-2">
									<span className="font-mono text-sm">{data?.code ?? t('customers.list.referrals.noCode')}</span>
									{data?.code && (
										<Button variant="ghost" size="icon" className="h-7 w-7" aria-label={t('customers.list.referrals.copy')} onClick={() => copy(data.code!)}>
											<Copy size={14} />
										</Button>
									)}
								</div>
								{data?.codeCreatedAt && (
									<p className="text-xs text-foreground-muted">{t('customers.list.referrals.createdOn', { date: dateOnly(data.codeCreatedAt) })}</p>
								)}
							</div>
							<div className="rounded-md border border-border px-3 py-2">
								<p className="text-xs text-foreground-muted">{t('customers.list.referrals.referredBy')}</p>
								{data?.referredBy ? (
									<button
										type="button"
										className="text-left text-sm text-primary hover:underline disabled:no-underline"
										disabled={!onOpenCustomer}
										onClick={() => onOpenCustomer?.(data.referredBy!.id!)}>
										{data.referredBy.name || data.referredBy.email} <span className="text-foreground-muted">({data.referredBy.email})</span>
									</button>
								) : (
									<p className="text-sm">—</p>
								)}
							</div>
						</div>

						{confirming ? (
							<div className="flex flex-col gap-2 rounded-md border border-warning-border bg-warning-subtle px-3 py-2 text-sm">
								<p>{t('customers.list.referrals.regenerateWarning')}</p>
								<div className="flex justify-end gap-2">
									<Button variant="ghost" size="sm" onClick={() => setConfirming(false)} disabled={regenerate.isPending}>
										{t('common.cancel')}
									</Button>
									<Button size="sm" onClick={doRegenerate} disabled={regenerate.isPending}>
										{t('customers.list.referrals.regenerate')}
									</Button>
								</div>
							</div>
						) : (
							<div className="flex justify-end">
								<Button size="sm" variant="outline" onClick={() => setConfirming(true)}>
									<RefreshCw size={15} /> {t('customers.list.referrals.regenerate')}
								</Button>
							</div>
						)}

						<div className="max-h-[50vh] overflow-auto rounded-md border border-border">
							{items.length === 0 ? (
								<div className="flex flex-col items-center gap-3 py-12 text-center">
									<Gift size={32} className="text-foreground-disabled" strokeWidth={1.25} />
									<p className="text-sm text-foreground-muted">{t('customers.list.referrals.none')}</p>
								</div>
							) : (
								<table className="w-full border-collapse text-sm">
									<thead className="bg-elevated text-xs text-foreground-muted">
										<tr className="border-b border-border">
											<th className="px-3 py-2 text-left font-medium">{t('customers.list.referrals.customer')}</th>
											<th className="px-3 py-2 text-left font-medium">{t('customers.list.referrals.email')}</th>
											<th className="px-3 py-2 text-left font-medium">{t('customers.list.referrals.joined')}</th>
											<th className="px-3 py-2 text-left font-medium">{t('customers.list.referrals.status')}</th>
										</tr>
									</thead>
									<tbody>
										{items.map(r => (
											<tr key={r.id} className="border-b border-border/60 hover:bg-muted/50">
												<td className="px-3 py-2 text-foreground">
													<button
														type="button"
														className="text-left hover:underline disabled:no-underline"
														disabled={!onOpenCustomer}
														onClick={() => onOpenCustomer?.(r.id!)}>
														{r.name || '—'}
													</button>
												</td>
												<td className="px-3 py-2 text-foreground-muted">{r.email}</td>
												<td className="px-3 py-2 tabular-nums text-foreground-muted">{dateOnly(r.joinedAt)}</td>
												<td className="px-3 py-2">{statusLabel(r.status, r.points)}</td>
											</tr>
										))}
									</tbody>
								</table>
							)}
						</div>
						{(data?.total ?? 0) > items.length && (
							<p className="text-xs text-foreground-muted">{t('customers.list.referrals.showingNewest', { shown: items.length, total: data?.total })}</p>
						)}
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
